import TopSection from "../../LT/TopSection"
import ExtractForecastFilterScreens from "./ExtractForecastFilterScreens";
// import dropdownData from "./ExtractForecastDataResponse.json";
import { Typography, Checkbox, FormControlLabel, Paper } from "@mui/material";
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import DatePicker from "../../../components/common/DatePicker";
import { useSelector, useDispatch } from "react-redux";
import { useState, useEffect, useCallback, useMemo } from "react";
import dayjs from 'dayjs';
import { toast } from "react-toastify";
import { getAllVendorDetailsExtractFromSAPEndpoint, getDropdownExtractForecastFromSAPEndpoint, getPlantsExtractForecastFromSAPEndpoint } from "../../../common/endpoints";
import { getDropdownExtractForecastFromSAPSlice } from "../../../redux/actions/getDropdownExtractForecastFromSAPSlice";
import { getAllVendorDetailsExtractFromSAP } from "../../../redux/actions/GetAllVendorForecastExtractFromSAPSlice";

const toPlantOption = (item) => {
    if (item == null) {
        return null;
    }

    if (typeof item === "string" || typeof item === "number") {
        const value = String(item);
        return { label: value, value, actualValue: value };
    }

    const code = item.plant ?? item.value ?? item.code ?? item.id;
    const name = item.plantName ?? item.name ?? item.label ?? "";

    if (!code && !name) {
        return null;
    }

    const normalizedCode = String(code ?? name);
    const normalizedName = String(name ?? "").trim();

    return {
        label: normalizedName ? `${normalizedCode} | ${normalizedName}` : normalizedCode,
        value: normalizedCode,
        actualValue: normalizedCode,
    };
};

export const ExtractForecastFromSAP = () => {
    const defaultFromDate = dayjs();
    const defaultToDate = defaultFromDate.add(1, "year");
    const dispatch = useDispatch();

    const [selectedTimePeriodFrom, setSelectedTimePeriodFrom] = useState(defaultFromDate);
    const [selectedTimePeriodTo, setSelectedTimePeriodTo] = useState(defaultToDate);
    const [sendEmailToPlanner, setSendEmailToPlanner] = useState(false);
    const currentMonthStart = dayjs().startOf('month');
    const [plantOptions, setPlantOptions] = useState([]);
    const [selectedPlant, setSelectedPlant] = useState(null);
    const [dropdownData, setDropdownData] = useState([]);
    const [dropdownVendorData, setDropdownVendorData] = useState([]);
    
    const [plantLoading, setPlantLoading] = useState(false);
    const [dependentDropdownLoading, setDependentDropdownLoading] = useState(false);
    const selectedRegion = useSelector((state) => state.setRegionPlantStore.selectedRegion);
    const plantNameOnly = useSelector((state) => state.setRegionPlantStore.selectedPlant);
    const selectedPlantForPayload = plantNameOnly
        ? plantNameOnly.split(" - ")[0]
        : null;
    const userRole = localStorage.getItem('userRole');
    const userEmail = localStorage.getItem("userEmail");

    const selectedPlantCodes = useMemo(() => (
        selectedPlant?.actualValue
            ? [String(selectedPlant.actualValue).trim()]
            : []
    ), [selectedPlant]);


    const handleDateChangeFrom = (date) => {
        setSelectedTimePeriodFrom(date);
        setSelectedTimePeriodTo(date?.add(1, "year"));
    };
    const handleDateChangeTo = (date) => {
        // console.log("Selected date To:", date);
        setSelectedTimePeriodTo(date);
    };
    const onRenderClearCallback = () => {
        // console.log("Clear callback executed");
    }
    const onRenderExecuteCallback = (response) => {
        if (response?.message) {
            toast.success(response.message);
        } else {
            toast.error("Unable to execute Extract Forecast from SAP.");
        }
    };

    const getPlantOptions = useCallback(async () => {
        setPlantLoading(true);
        try {
            const response = await dispatch(
                getDropdownExtractForecastFromSAPSlice({
                    dynamicEndpoint: getPlantsExtractForecastFromSAPEndpoint,
                    params: {
                        userEmail: userEmail,
                        plant: selectedPlantForPayload || "ALL",
                        region: ['Global Planner', 'App Admin'].includes(userRole) ? "ALL" : (selectedRegion || "ALL")
                    }
                })
            );

            if (response?.meta?.requestStatus === "fulfilled") {
                const safePayload = Array.isArray(response?.payload) ? response.payload : [];

                const options = safePayload
                    .map(toPlantOption)
                    .filter(Boolean)
                    .filter((option, index, arr) => arr.findIndex((item) => item.value === option.value) === index)
                    .sort((a, b) => a.label.localeCompare(b.label));

                setPlantOptions(options);
            } else {
                toast.error("Failed to load plants for Extract Forecast from SAP.");
            }
        } catch (error) {
            console.error("Plant API error:", error);
            toast.error("Failed to load plants for Extract Forecast from SAP.");
        } finally {
            setPlantLoading(false);
        }
    }, [dispatch]);

    const getDropdownDataByPlant = useCallback(async (plantCodes) => {
        if (!Array.isArray(plantCodes) || plantCodes.length === 0) {
            setDropdownData([]);
            return;
        }

        setDependentDropdownLoading(true);
        try {
            const response = await dispatch(
                getDropdownExtractForecastFromSAPSlice({
                    dynamicEndpoint: getDropdownExtractForecastFromSAPEndpoint,
                    requestMethod: "POST",
                    data: {
                        plants: plantCodes,
                    },
                })
            );

            if (response?.meta?.requestStatus === "fulfilled") {
                const safePayload = Array.isArray(response?.payload) ? response.payload : [];
                const selectedPlantsSet = new Set(plantCodes.map((code) => String(code).trim()));

                const plantFilteredPayload = safePayload.filter((row) => {
                    const rowPlant = row?.plant;
                    if (rowPlant == null || rowPlant === "") {
                        return true;
                    }

                    return selectedPlantsSet.has(String(rowPlant).trim());
                });

                const normalizedPayload = plantFilteredPayload.map((row) => ({
                    ...row,
                    tccMaterialNumber: row?.tccMaterialNumber ?? row?.material ?? "",
                }));

                const relatedMaterials = Array.from(
                    new Set(
                        normalizedPayload
                            .map((row) => row?.tccMaterialNumber ?? row?.material)
                            .filter((item) => item != null && item !== "")
                    )
                );

                console.info(
                    `[ExtractForecastFromSAP] Plants ${plantCodes.join(", ")}: ${plantFilteredPayload.length} rows, ${relatedMaterials.length} unique materials`
                );

                setDropdownData(normalizedPayload);
            } else {
                setDropdownData([]);
                toast.error("Failed to load  filter data.");
            }
        } catch (error) {
            console.error("Dropdown API error:", error);
            setDropdownData([]);
            toast.error("Failed to load  filter data.");
        } finally {
            setDependentDropdownLoading(false);
        }
    }, [dispatch]);

    useEffect(() => {
        getPlantOptions();
    }, [getPlantOptions]);

    useEffect(() => {
        getDropdownDataByPlant(selectedPlantCodes);
    }, [selectedPlantCodes, getDropdownDataByPlant]);

    const getVendorDetailsAPI = useCallback(async () => {
        try {
            const response = await dispatch(
                getAllVendorDetailsExtractFromSAP({
                    dynamicEndpoint: getAllVendorDetailsExtractFromSAPEndpoint,
                })
            );
            if (response?.meta?.requestStatus === "fulfilled") {
                const safePayload = Array.isArray(response?.payload) ? response.payload : [];
                setDropdownVendorData(safePayload);
            } else {
                setDropdownVendorData([]);
                console.error("Failed to load vendor details for Extract Forecast from SAP.");
            }

        } catch (error) {
            console.error("Dropdown API error:", error);
            setDropdownData([]);
        }

    }, [dispatch]);

    useEffect(() => {
        getVendorDetailsAPI();
    }, [getVendorDetailsAPI]);



    return (
        <div>
            <Paper elevation={0} sx={{ border: "1px solid #d9d9d9", p: 2, mt: 2, backgroundColor: "#fafafa" }}>
                <ExtractForecastFilterScreens
                    plantOptions={plantOptions}
                    selectedPlant={selectedPlant}
                    onPlantChange={setSelectedPlant}
                    dropdownData={dropdownData}
                    dropdownVendorData={dropdownVendorData}
                    plantLoading={plantLoading}
                    dependentDropdownLoading={dependentDropdownLoading}
                    onRenderClearCallback={onRenderClearCallback}
                    onRenderExecuteCallback={onRenderExecuteCallback}
                    selectedTimePeriodFrom={selectedTimePeriodFrom}
                    selectedTimePeriodTo={selectedTimePeriodTo}
                    sendEmailToPlanner={sendEmailToPlanner}
                />
            </Paper>
            <Paper elevation={0} sx={{ border: "1px solid #d9d9d9", p: 2, mt: 3 }}>
                <Typography sx={{ fontSize: 16, fontWeight: 500 }}>Forecast Time Horizon</Typography>
                <div>
                    <div >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '50%', marginTop: 10 }}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <div style={{ display: 'flex', alignItems: 'center', }}>
                                    <p style={{ marginRight: 100, fontSize: 13 }}>Time Period</p>
                                    <DatePicker
                                        name="Starting From"
                                        value={selectedTimePeriodFrom} // Ensure it's a dayjs object
                                        onChange={handleDateChangeFrom}
                                        height="30px"
                                        width={150}
                                        fontSize={13}
                                        fontWeight="normal"
                                        styles={{
                                            fontSize: '12px',
                                            fontWeight: 'normal',
                                        }}
                                        minDate={currentMonthStart}  // Disable previous months
                                    // maxDate={nextMonthEnd}  // Allow only dates from current to next month
                                    />
                                </div>
                            </LocalizationProvider>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <p style={{ marginRight: 10, fontSize: 13 }}>To</p>
                                    <DatePicker
                                        name="TimePeriodTo"
                                        value={selectedTimePeriodTo} // Ensure it's a dayjs object
                                        onChange={handleDateChangeTo}
                                        height="30px"
                                        width={150}
                                        fontSize={13}
                                        fontWeight="normal"
                                        styles={{
                                            fontSize: '12px',
                                            fontWeight: 'normal',
                                        }}
                                        minDate={selectedTimePeriodFrom}
                                    />
                                </div>
                            </LocalizationProvider>
                        </div>

                    </div>
                    {/* <div>
                        <div style={{ display: 'flex', alignItems: 'center', }}>
                            <p style={{ marginRight: 36, fontSize: 13 }}>Weeks to send to Ariba</p>
                            <TextField
                                value={noOfWeeks}
                                onChange={(e) => {
                                    setNoOfWeeks(e.target.value);
                                }}
                                sx={{
                                    width: 150,
                                    '& .MuiInputBase-root': {
                                        height: 30, // same like DatePicker height
                                    },
                                }}
                            />
                        </div>
                    </div> */}
                    <div style={{ marginTop: 10 }}>
                        <FormControlLabel
                            sx={{
                                '& .MuiFormControlLabel-label': {
                                    fontSize: '13px',
                                },
                            }}
                            control={
                                <Checkbox
                                    checked={sendEmailToPlanner}
                                    onChange={(e) => setSendEmailToPlanner(e.target.checked)}
                                    sx={{ fontSize: 13 }}
                                />
                            }
                            label="Send email to Planner"
                        />
                    </div>
                </div>
            </Paper>
        </div>
    )
}
